import { bind } from '@react-rxjs/core'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  map,
  of,
  startWith,
} from 'rxjs'

export type HeaderStyle = 'lightBlue' | 'white' | 'transparent'

export interface HeaderStyleClasses {
  backgroundColor: string
  textColor: string
}

const defaultHeaderStyle: HeaderStyle = 'white'

export const whiteHeaderStyleClasses: HeaderStyleClasses = {
  backgroundColor: 'bg-white',
  textColor: 'text-brand-navy',
}

export const lightBlueHeaderStyleClasses: HeaderStyleClasses = {
  backgroundColor: 'bg-brand-navy-3',
  textColor: 'text-brand-navy',
}

export const transparentHeaderStyleClasses: HeaderStyleClasses = {
  backgroundColor: 'bg-transparent',
  textColor: 'text-brand-navy',
}

export const stringifyHeaderStyle = (headerStyle: HeaderStyleClasses): string => {
  return `${headerStyle.backgroundColor} ${headerStyle.textColor}`
}

export const isValidHeaderStyle = (
  headerStyle: string | null | undefined,
): headerStyle is HeaderStyle => {
  const options = ['lightBlue', 'white', 'transparent']
  return !!headerStyle && options.includes(headerStyle)
}

export const parseHeaderStyleClasses = (headerStyle: HeaderStyle): HeaderStyleClasses => {
  return headerStyle === 'transparent'
    ? transparentHeaderStyleClasses
    : headerStyle === 'white'
      ? whiteHeaderStyleClasses
      : lightBlueHeaderStyleClasses
}

// base header style

const headerStyleBaseSubject = new BehaviorSubject<HeaderStyle>(defaultHeaderStyle)
export const headerStyleBase$ = headerStyleBaseSubject.asObservable()
export const setHeaderStyleBase = (style: HeaderStyle) => {
  headerStyleBaseSubject.next(style)
}

export const [useHeaderStyleBase] = bind(headerStyleBaseSubject, defaultHeaderStyle)

// active header style (i.e. considering scroll)

const headerScrollOffset = 10

const scrollDistance$ =
  typeof window === 'undefined'
    ? of(0)
    : fromEvent(window, 'scroll').pipe(
        map(() => window.scrollY),
        startWith(window.scrollY),
      )

export const headerStyle$ = combineLatest([scrollDistance$, headerStyleBase$]).pipe(
  map(
    ([scrollDistance, headerStyle]) => [scrollDistance >= headerScrollOffset, headerStyle] as const,
  ),
  map(([isScrolled, headerStyle]) =>
    isScrolled && headerStyle === 'transparent' ? ('white' satisfies HeaderStyle) : headerStyle,
  ),
  distinctUntilChanged(),
  map((headerStyle): { headerStyle: HeaderStyle; classes: HeaderStyleClasses } => ({
    headerStyle,
    classes: parseHeaderStyleClasses(headerStyle),
  })),
)

export const [useHeaderStyle] = bind(headerStyle$, {
  headerStyle: defaultHeaderStyle,
  classes: parseHeaderStyleClasses(defaultHeaderStyle),
})
